import * as THREE from 'three';
import type { SectionPickTarget } from '../ride/track-visual.ts';

export type PointerPickMode = 'none' | 'node' | 'section' | 'both' | 'scenery';

export interface PointerViewport {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export function isPlacementClick(
  start: { readonly x: number; readonly y: number },
  end: { readonly x: number; readonly y: number },
  tolerancePixels = 7,
): boolean {
  return Math.hypot(end.x - start.x, end.y - start.y) <= tolerancePixels;
}

export function pickNearestSection(
  targets: readonly SectionPickTarget[],
  camera: THREE.Camera,
  viewport: PointerViewport,
  pointer: { readonly x: number; readonly y: number },
  tolerancePixels: number,
): string | null {
  if (viewport.width <= 0 || viewport.height <= 0 || tolerancePixels <= 0) return null;
  const projectedA = new THREE.Vector3();
  const projectedB = new THREE.Vector3();
  let bestId: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestDepth = Number.POSITIVE_INFINITY;
  const project = (source: THREE.Vector3, target: THREE.Vector3) => {
    target.copy(source).project(camera);
    return {
      x: viewport.left + (target.x + 1) * 0.5 * viewport.width,
      y: viewport.top + (1 - target.y) * 0.5 * viewport.height,
      z: target.z,
    };
  };
  for (const target of targets) {
    for (let index = 1; index < target.points.length; index += 1) {
      const a = project(target.points[index - 1]!, projectedA);
      const b = project(target.points[index]!, projectedB);
      if (a.z < -1 || a.z > 1 || b.z < -1 || b.z > 1) continue;
      const right = viewport.left + viewport.width;
      const bottom = viewport.top + viewport.height;
      if (
        (a.x < viewport.left && b.x < viewport.left) ||
        (a.x > right && b.x > right) ||
        (a.y < viewport.top && b.y < viewport.top) ||
        (a.y > bottom && b.y > bottom)
      )
        continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const lengthSquared = dx * dx + dy * dy;
      const interpolation =
        lengthSquared > 0
          ? THREE.MathUtils.clamp(
              ((pointer.x - a.x) * dx + (pointer.y - a.y) * dy) / lengthSquared,
              0,
              1,
            )
          : 0;
      const closestX = a.x + dx * interpolation;
      const closestY = a.y + dy * interpolation;
      const distance = Math.hypot(pointer.x - closestX, pointer.y - closestY);
      if (distance > tolerancePixels) continue;
      const depth = THREE.MathUtils.lerp(a.z, b.z, interpolation);
      const materiallyCloser = distance < bestDistance - 2;
      const depthTie = Math.abs(distance - bestDistance) <= 2 && depth < bestDepth;
      if (!materiallyCloser && !depthTie) continue;
      bestId = target.sectionId;
      bestDistance = distance;
      bestDepth = depth;
    }
  }
  return bestId;
}

export interface EditableHandle extends THREE.Mesh {
  userData: {
    nodeId: string;
    [key: string]: unknown;
  };
}

export interface PointerEditorCallbacks {
  canEdit(): boolean;
  getPlacementGroundY?(): number | null;
  onPlacementPointer?(position: THREE.Vector3, commit: boolean): void;
  getPickMode?(): PointerPickMode;
  getTrackRoot?(): THREE.Object3D | null;
  getSectionPickTargets?(): readonly SectionPickTarget[];
  getSceneryRoot?(): THREE.Object3D | null;
  onSelect(nodeId: string, additive: boolean): void;
  onSelectSection?(sectionId: string): void;
  onSelectScenery?(sceneryId: string): void;
  onMarquee?(bounds: { left: number; top: number; right: number; bottom: number }): void;
  onHover?(target: { kind: 'node' | 'section'; id: string } | null): void;
  onMove(nodeId: string, position: THREE.Vector3): void;
  onCommit(nodeId: string, position: THREE.Vector3): void;
  onEnd(changed: boolean): void;
}

export class PointerEditor {
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0));
  private readonly hit = new THREE.Vector3();
  private selected: EditableHandle | null = null;
  private didMove = false;
  private placementPointerStart: { x: number; y: number; pointerId: number } | null = null;
  private marqueeStart: { x: number; y: number; pointerId: number } | null = null;
  private marqueeElement: HTMLDivElement | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: THREE.Camera,
    private readonly handles: THREE.Group,
    private readonly callbacks: PointerEditorCallbacks,
  ) {
    canvas.addEventListener('pointerdown', this.onPointerDown, { capture: true });
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown, true);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.classList.remove('is-hovering-track-piece');
    this.clearMarquee();
  }

  clearHover(): void {
    this.setSectionHover(false);
  }

  private setPointer(event: PointerEvent): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.callbacks.canEdit() || event.button !== 0) return;
    this.setPointer(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const placementGroundY = this.callbacks.getPlacementGroundY?.();
    if (placementGroundY !== null && placementGroundY !== undefined) {
      this.dragPlane.constant = -placementGroundY;
      if (this.raycaster.ray.intersectPlane(this.dragPlane, this.hit)) {
        this.placementPointerStart = {
          x: event.clientX,
          y: event.clientY,
          pointerId: event.pointerId,
        };
        this.callbacks.onPlacementPointer?.(this.hit.clone(), false);
      }
      return;
    }
    const pickMode = this.callbacks.getPickMode?.() ?? 'both';
    if (pickMode === 'none') return;
    const intersection =
      pickMode !== 'node' && pickMode !== 'both'
        ? undefined
        : this.raycaster.intersectObjects(this.handles.children, false)[0];
    if (!intersection) {
      if (
        event.shiftKey &&
        (pickMode === 'node' || pickMode === 'both') &&
        this.callbacks.onMarquee
      ) {
        this.marqueeStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
        this.marqueeElement = document.createElement('div');
        this.marqueeElement.className = 'marquee-selection';
        document.body.append(this.marqueeElement);
        this.updateMarquee(event.clientX, event.clientY);
        this.canvas.setPointerCapture(event.pointerId);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const sceneryRoot = pickMode === 'scenery' ? this.callbacks.getSceneryRoot?.() : null;
      const sceneryHit = sceneryRoot
        ? this.raycaster
            .intersectObject(sceneryRoot, true)
            .find((candidate) => candidate.object.userData.sceneryId)
        : null;
      const sceneryId = sceneryHit?.object.userData.sceneryId;
      if (typeof sceneryId === 'string') {
        this.callbacks.onSelectScenery?.(sceneryId);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const sectionId =
        pickMode === 'section' || pickMode === 'both' ? this.pickSection(event) : null;
      if (sectionId) {
        this.callbacks.onSelectSection?.(sectionId);
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }
    this.selected = intersection.object as EditableHandle;
    this.didMove = false;
    this.dragPlane.constant = -this.selected.position.y;
    this.canvas.setPointerCapture(event.pointerId);
    this.callbacks.onSelect(this.selected.userData.nodeId, event.shiftKey);
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.callbacks.canEdit()) return;
    const placementGroundY = this.callbacks.getPlacementGroundY?.();
    if (placementGroundY !== null && placementGroundY !== undefined) {
      this.setPointer(event);
      this.raycaster.setFromCamera(this.pointer, this.camera);
      this.dragPlane.constant = -placementGroundY;
      if (this.raycaster.ray.intersectPlane(this.dragPlane, this.hit))
        this.callbacks.onPlacementPointer?.(this.hit.clone(), false);
      return;
    }
    if (this.marqueeStart) {
      this.updateMarquee(event.clientX, event.clientY);
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (!this.selected) {
      this.setPointer(event);
      this.raycaster.setFromCamera(this.pointer, this.camera);
      const pickMode = this.callbacks.getPickMode?.() ?? 'both';
      if (pickMode === 'none' || pickMode === 'scenery') {
        this.setSectionHover(false);
        this.callbacks.onHover?.(null);
        return;
      }
      const handleHit =
        pickMode !== 'node' && pickMode !== 'both'
          ? undefined
          : this.raycaster.intersectObjects(this.handles.children, false)[0];
      if (handleHit) {
        this.setSectionHover(false);
        this.callbacks.onHover?.({ kind: 'node', id: String(handleHit.object.userData.nodeId) });
        return;
      }
      const sectionId =
        pickMode === 'section' || pickMode === 'both' ? this.pickSection(event) : null;
      this.setSectionHover(Boolean(sectionId));
      this.callbacks.onHover?.(sectionId ? { kind: 'section', id: sectionId } : null);
      return;
    }
    this.setPointer(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    if (!this.raycaster.ray.intersectPlane(this.dragPlane, this.hit)) return;
    this.selected.position.x = this.hit.x;
    this.selected.position.z = this.hit.z;
    this.didMove = true;
    this.callbacks.onMove(this.selected.userData.nodeId, this.selected.position.clone());
    event.preventDefault();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.placementPointerStart?.pointerId === event.pointerId) {
      const start = this.placementPointerStart;
      this.placementPointerStart = null;
      if (event.type === 'pointerup' && isPlacementClick(start, event)) {
        const placementGroundY = this.callbacks.getPlacementGroundY?.();
        if (placementGroundY !== null && placementGroundY !== undefined) {
          this.setPointer(event);
          this.raycaster.setFromCamera(this.pointer, this.camera);
          this.dragPlane.constant = -placementGroundY;
          if (this.raycaster.ray.intersectPlane(this.dragPlane, this.hit))
            this.callbacks.onPlacementPointer?.(this.hit.clone(), true);
        }
      }
      return;
    }
    if (this.marqueeStart) {
      const { x, y, pointerId } = this.marqueeStart;
      if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId);
      this.callbacks.onMarquee?.({
        left: Math.min(x, event.clientX),
        top: Math.min(y, event.clientY),
        right: Math.max(x, event.clientX),
        bottom: Math.max(y, event.clientY),
      });
      this.clearMarquee();
      event.preventDefault();
      return;
    }
    if (!this.selected) return;
    const released = this.selected;
    this.selected = null;
    if (this.canvas.hasPointerCapture(event.pointerId))
      this.canvas.releasePointerCapture(event.pointerId);
    const changed = this.didMove;
    if (changed) this.callbacks.onCommit(released.userData.nodeId, released.position.clone());
    this.didMove = false;
    this.callbacks.onEnd(changed);
  };

  private updateMarquee(clientX: number, clientY: number): void {
    if (!this.marqueeStart || !this.marqueeElement) return;
    const left = Math.min(this.marqueeStart.x, clientX);
    const top = Math.min(this.marqueeStart.y, clientY);
    this.marqueeElement.style.left = `${left}px`;
    this.marqueeElement.style.top = `${top}px`;
    this.marqueeElement.style.width = `${Math.abs(clientX - this.marqueeStart.x)}px`;
    this.marqueeElement.style.height = `${Math.abs(clientY - this.marqueeStart.y)}px`;
  }

  private clearMarquee(): void {
    this.marqueeStart = null;
    this.marqueeElement?.remove();
    this.marqueeElement = null;
  }

  private pickSection(event: PointerEvent): string | null {
    const trackRoot = this.callbacks.getTrackRoot?.();
    const exact = trackRoot
      ? this.raycaster
          .intersectObject(trackRoot, true)
          .find((candidate) => candidate.object.userData.sectionId)
      : null;
    const exactId = exact?.object.userData.sectionId;
    if (typeof exactId === 'string') return exactId;
    const targets = this.callbacks.getSectionPickTargets?.() ?? [];
    const bounds = this.canvas.getBoundingClientRect();
    const coarsePointer =
      event.pointerType === 'touch' ||
      globalThis.matchMedia?.('(pointer: coarse)').matches === true;
    return pickNearestSection(
      targets,
      this.camera,
      bounds,
      { x: event.clientX, y: event.clientY },
      coarsePointer ? 22 : 14,
    );
  }

  private setSectionHover(active: boolean): void {
    this.canvas.classList.toggle('is-hovering-track-piece', active);
  }
}
